import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../ui/Input';
import {
  DetailSection,
  DetailField,
  SECTION_CARD_HEADER,
  SECTION_CARD_HEADER_MAIN,
  SECTION_CARD_ICON,
  SECTION_CARD_TITLE_COL,
  SECTION_CARD_TITLE,
  SECTION_CARD_ACTIONS,
} from '../ui/DetailSection';
import { Button } from '../ui/Button';
import { SearchableSelect } from '../ui/SearchableSelect';
import { AuthenticatedImage } from '../ui/AuthenticatedImage';
import { AuthenticatedFileViewLink } from '../ui/AuthenticatedFileLink';
import { BeamTableColorPicker } from '../BeamTableColorPicker';
import {
  DEFAULT_BEAM_ROW_QTY,
  STAGE_CONTEXT_EMPTY,
  WORKFLOW_SHOWS_BUTTA_CUTTING,
  WORKFLOW_SHOWS_MILL_FINISH,
  WORKFLOW_SHOWS_BORDER_CUTTING,
  WORKFLOW_SHOWS_HANDWORK,
  beamPiecesTotal,
  feederHeaderShort,
  mergeBeamFeedersWithDesign,
  sortDesignFeeders,
} from './orderFormHelpers';
import { beamStatusMeta, isBeamTerminalStatus } from './workflowConstants';
import { formatPartyAddressSummary } from '../PartyMaster';
import { BeamPlanCloneModal } from './BeamPlanCloneModal';
import { DesignImagePreviewModal } from './DesignImagePreviewModal';

// ---------- Shared classes (match wizard styling so nothing looks off) -------
const FIELD_LABEL = 'mb-1.5 block text-sm font-medium text-gray-800';
const FIELD_INPUT_WRAP =
  '[&_label]:mb-1.5 [&_label]:text-sm [&_label]:font-medium [&_label]:text-gray-800 [&_input]:text-base [&_input]:px-3 [&_input]:py-2.5 [&_input]:rounded-lg';
/** Label stack only — do not set input padding (conflicts with icon inset + pl-* on custom inputs). */
const FIELD_LABEL_STACK = '[&_label]:mb-1.5 [&_label]:block [&_label]:text-sm [&_label]:font-medium [&_label]:text-gray-800';

// Beam grid cell classes (kept identical to wizard step 3)
const BT_TH = 'border border-slate-400 bg-slate-100 px-1 py-2.5 text-center text-[11px] font-bold text-slate-900 sm:px-2 sm:text-xs';
/** Sticky headers when the grid scrolls vertically inside its max-height box. */
const BT_TH_STICKY = `${BT_TH} sticky top-0 z-10 bg-slate-100 shadow-[0_1px_0_0_rgb(203_213_225)]`;
const BT_TD = 'border border-slate-300 bg-white px-1 py-2 text-center align-middle sm:px-2';
const BT_TD_NUM = `${BT_TD} min-w-[2.75rem] text-sm font-bold text-slate-800 sm:min-w-[3rem]`;
const BT_TD_BEAM = `${BT_TD} min-w-[9.5rem] sm:min-w-[10.5rem]`;
const BT_TD_FEED = `${BT_TD} min-w-[7.25rem] sm:min-w-[8rem]`;
const BT_TD_PCS = `${BT_TD} min-w-[3.5rem] sm:min-w-[4rem]`;
const BT_TD_UNIT = `${BT_TD} min-w-[3rem] sm:min-w-[3.5rem]`;
const BT_TD_TAKA = `${BT_TD} min-w-[4rem] sm:min-w-[4.5rem]`;
const BT_TD_RPCS = `${BT_TD} min-w-[3.25rem] sm:min-w-[3.5rem]`;
const BT_TD_STATUS = `${BT_TD} w-[10.25rem] min-w-[10.25rem] max-w-[10.25rem] px-1.5 py-2 text-center align-middle sm:w-[11rem] sm:min-w-[11rem] sm:max-w-[11rem] sm:px-2`;
const BT_TD_TOTAL = 'border border-slate-400 bg-slate-100 px-2 py-2.5 text-center font-bold text-slate-900 sm:px-3';
/** Sticks to the bottom of the scroll box when more than BEAM_VISIBLE_ROW_CAP data rows. */
const BT_TD_TOTAL_STICKY = `${BT_TD_TOTAL} sticky bottom-0 z-10 bg-slate-100 shadow-[0_-1px_0_0_rgb(203_213_225)]`;

/** Header + footer + this many beam rows fit without inner scroll; row 13+ scrolls inside the box. */
const BEAM_VISIBLE_ROW_CAP = 12;

/**
 * ≤12 data rows: natural height (header + rows + TOTAL), page scrolls normally.
 * >12 data rows: cap height to header + 12 rows + TOTAL; sticky header/footer inside the box.
 */
const BEAM_SCROLL_BASE = [
  'overflow-x-auto rounded-xl',
  '[scrollbar-width:thin] [scrollbar-color:rgb(148_163_184)_transparent]',
  '[&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5',
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300',
  '[&::-webkit-scrollbar-track]:bg-slate-50',
].join(' ');

const BEAM_SCROLL_COMPACT = `${BEAM_SCROLL_BASE} overflow-y-visible`;
const BEAM_SCROLL_OVERFLOW = `${BEAM_SCROLL_BASE} overflow-y-auto overscroll-y-auto`;

function BeamTableScrollArea({ children, dataRowCount = 0 }) {
  const ref = useRef(null);
  const [maxHeightPx, setMaxHeightPx] = useState(null);
  const needsScroll = dataRowCount > BEAM_VISIBLE_ROW_CAP;

  const measure = useCallback(() => {
    const root = ref.current;
    if (!root || dataRowCount <= BEAM_VISIBLE_ROW_CAP) {
      setMaxHeightPx(null);
      return;
    }
    const thead = root.querySelector('thead');
    const totalRow = root.querySelector('[data-beam-totals]');
    const dataRows = root.querySelectorAll('[data-beam-row]');
    let rowsH = 0;
    const visibleCap = Math.min(BEAM_VISIBLE_ROW_CAP, dataRows.length);
    for (let i = 0; i < visibleCap; i += 1) {
      rowsH += dataRows[i].getBoundingClientRect().height;
    }
    const headerH = thead?.getBoundingClientRect().height ?? 0;
    const footerH = totalRow?.getBoundingClientRect().height ?? 0;
    setMaxHeightPx(Math.ceil(headerH + rowsH + footerH + 1));
  }, [dataRowCount]);

  useLayoutEffect(() => {
    measure();
  }, [measure, children]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const table = el.querySelector('table');
    if (table) ro.observe(table);
    el.querySelectorAll('[data-beam-row]').forEach((row) => ro.observe(row));
    window.addEventListener('resize', measure, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, children, dataRowCount]);

  const onWheel = useCallback((e) => {
    const el = e.currentTarget;
    if (e.shiftKey && el.scrollWidth > el.clientWidth + 2) {
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  }, []);

  return (
    <div
      ref={ref}
      className={needsScroll ? BEAM_SCROLL_OVERFLOW : BEAM_SCROLL_COMPACT}
      style={needsScroll && maxHeightPx ? { maxHeight: maxHeightPx } : undefined}
      onWheel={onWheel}
    >
      {children}
    </div>
  );
}

// ============================================================================
// PartySection — party + delivery pickers (or read-only)
// ============================================================================
export function PartySection({
  readOnly,
  form,
  setForm,
  parties,
  partySnapshot,
  onAddParty,
}) {
  const selectedParty = useMemo(
    () => parties.find((p) => String(p.id) === String(form.partyId)),
    [parties, form.partyId]
  );

  if (readOnly) {
    const name = partySnapshot?.partyName || selectedParty?.name || '—';
    const contact = partySnapshot?.partyMobile || selectedParty?.contact || '';
    const address =
      partySnapshot?.partyAddress ||
      formatPartyAddressSummary(selectedParty) ||
      '';
    return (
      <DetailSection
        title="Party"
        icon={(
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )}
        columns={2}
      >
        <DetailField label="Name" value={name} />
        <DetailField label="Contact" value={contact} />
        <DetailField label="Address" value={address} span="full" />
      </DetailSection>
    );
  }

  return (
    <DetailSection
      title="Party"
      icon={(
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )}
      columns={2}
    >
      <div className="sm:col-span-2">
        <label className={FIELD_LABEL}>Customer party *</label>
        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <SearchableSelect
              value={form.partyId}
              placeholder={parties.length ? 'Type to find party…' : 'No parties yet'}
              emptyMessage="No party starts with that text"
              options={(parties || []).map((p) => ({ value: String(p.id), label: p.name || '' }))}
              onChange={(id) =>
                setForm((w) => ({ ...w, partyId: id, deliveryDetailId: '' }))
              }
            />
          </div>
          {onAddParty ? (
            <Button type="button" variant="outline" size="sm" className="shrink-0 self-start" onClick={onAddParty}>
              + Add
            </Button>
          ) : null}
        </div>
      </div>
    </DetailSection>
  );
}

// ============================================================================
// KhataSection — production unit picker (API: khata)
// ============================================================================
export function KhataSection({ readOnly, form, setForm, khatas, khataSnapshot }) {
  const selected = (khatas || []).find((k) => String(k.id) === String(form.khataId));
  const displayName =
    khataSnapshot?.khataName ||
    form.khataName ||
    selected?.name ||
    '—';

  if (readOnly) {
    return (
      <DetailSection
        title="Production Units"
        icon={(
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        )}
        columns={2}
      >
        <DetailField label="Production unit" value={displayName} span="full" />
      </DetailSection>
    );
  }

  const activeKhatas = (khatas || []).filter((k) => k.active !== false);

  return (
    <DetailSection
      title="Production Units"
      icon={(
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )}
      columns={2}
    >
      <div className="sm:col-span-2">
        <label className={FIELD_LABEL} htmlFor="order-khata-select">
          Production unit *
        </label>
        <SearchableSelect
          id="order-khata-select"
          value={form.khataId}
          placeholder={activeKhatas.length ? 'Type to find production unit…' : 'No production units yet'}
          emptyMessage="No production unit starts with that text"
          options={activeKhatas.map((k) => ({ value: String(k.id), label: k.name || '' }))}
          onChange={(id) => {
            const k = activeKhatas.find((x) => String(x.id) === id);
            setForm((w) => ({
              ...w,
              khataId: id,
              khataName: k?.name || '',
            }));
          }}
        />
      </div>
    </DetailSection>
  );
}

// ============================================================================
// TransportSection — transporter / transport service (for delivery)
// ============================================================================
export function TransportSection({ readOnly, form, setForm, transportParties, transportSnapshot, onAddTransport }) {
  const list = transportParties || [];
  const selected = list.find((p) => String(p.id) === String(form.transportPartyId));
  const displayName =
    transportSnapshot?.transportServiceName ||
    form.transportServiceName ||
    selected?.name ||
    '—';

  if (readOnly) {
    return (
      <DetailSection
        title="Transport service"
        icon={(
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        )}
        columns={2}
      >
        <DetailField label="Transport service name" value={displayName} span="full" />
        {selected?.contact ? <DetailField label="Transporter contact" value={selected.contact} /> : null}
        {selected?.address ? <DetailField label="Transporter address" value={selected.address} span="full" /> : null}
      </DetailSection>
    );
  }

  return (
    <DetailSection
      title="Transport service"
      icon={(
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )}
      columns={2}
    >
      <div className="sm:col-span-2">
        <label className={FIELD_LABEL} htmlFor="order-transport-select">
          Transport *
        </label>
        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <SearchableSelect
              id="order-transport-select"
              value={form.transportPartyId}
              placeholder={list.length ? 'Type to find transport…' : 'No transport parties yet'}
              emptyMessage="No transport starts with that text"
              options={list.map((p) => ({ value: String(p.id), label: p.name || '' }))}
              onChange={(id) => {
                const p = list.find((x) => String(x.id) === id);
                setForm((w) => ({
                  ...w,
                  transportPartyId: id,
                  transportServiceName: p?.name || '',
                }));
              }}
            />
          </div>
          {onAddTransport ? (
            <Button type="button" variant="outline" size="sm" className="shrink-0 self-start" onClick={onAddTransport}>
              + Add
            </Button>
          ) : null}
        </div>
      </div>
    </DetailSection>
  );
}

// ============================================================================
// BrokerSection — broker linked to the order (master: Users → Brokers)
// ============================================================================
export function BrokerSection({ readOnly, form, setForm, brokers, brokerSnapshot, onAddBroker }) {
  const list = brokers || [];
  const selected = list.find((b) => String(b.id) === String(form.brokerId));
  const displayName =
    brokerSnapshot?.brokerName ||
    form.brokerName ||
    selected?.name ||
    '—';

  if (readOnly) {
    return (
      <DetailSection
        title="Broker"
        icon={(
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )}
        columns={2}
      >
        <DetailField label="Broker" value={displayName} span="full" />
        {selected?.contact ? <DetailField label="Contact" value={selected.contact} /> : null}
      </DetailSection>
    );
  }

  return (
    <DetailSection
      title="Broker"
      icon={(
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )}
      columns={2}
    >
      <div className="sm:col-span-2">
        <label className={FIELD_LABEL} htmlFor="order-broker-select">
          Broker *
        </label>
        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <SearchableSelect
              id="order-broker-select"
              value={form.brokerId}
              placeholder={list.length ? 'Type to find broker…' : 'No brokers yet'}
              emptyMessage="No broker starts with that text"
              options={list.map((b) => ({ value: String(b.id), label: b.name || '' }))}
              onChange={(id) => {
                const b = list.find((x) => String(x.id) === id);
                setForm((w) => ({
                  ...w,
                  brokerId: id,
                  brokerName: b?.name || '',
                }));
              }}
            />
          </div>
          {onAddBroker ? (
            <Button type="button" variant="outline" size="sm" className="shrink-0 self-start" onClick={onAddBroker}>
              + Add
            </Button>
          ) : null}
        </div>
      </div>
    </DetailSection>
  );
}

// ============================================================================
// DesignSection — searchable design picker (or read-only summary)
//
// Edit-mode UX (DesignPicker):
//   - Selected design shown as a prominent banner card with "Change" button.
//   - Picker panel (shown when nothing selected or "Change" pressed) has:
//       • Optional autofocus on search (off on brand-new orders so the page stays scrolled to top)
//       • Dense vertical list (one row per design) — not a grid — so more
//         options are visible per screen and scrolling is short.
//       • Full keyboard navigation (↑/↓ highlight, Enter to pick, Esc to close)
//       • Result counter and a soft cap (150) with "refine search" hint to
//         stay fast on datasets with many designs.
// ============================================================================

const MAX_PICKER_ROWS = 150;

export function DesignSection({
  readOnly,
  form,
  setForm,
  designs,
  designSnapshot,
  /** When false, the search field is not auto-focused (avoids jumping scroll on create order). */
  autoFocusSearch = true,
  onCreateDesign,
}) {
  const selectedDesign = useMemo(
    () => designs.find((d) => String(d.id) === String(form.designId)),
    [designs, form.designId]
  );

  if (readOnly) {
    return (
      <DesignSectionReadOnly
        designSnapshot={designSnapshot}
        selectedDesign={selectedDesign}
      />
    );
  }

  return (
    <DesignPicker
      designs={designs}
      selectedDesign={selectedDesign}
      autoFocusSearch={autoFocusSearch}
      onCreateDesign={onCreateDesign}
      onPick={(d) =>
        setForm((w) => {
          const next = {
            ...w,
            designId: String(d.id),
            beamPlan: null,
            beamPlanSourceDesignId: null,
          };
          if (d?.sampleDesign === true) {
            next.partyId = '';
            next.deliveryDetailId = '';
            next.brokerId = '';
            next.brokerName = '';
            next.transportPartyId = '';
            next.transportServiceName = '';
            next.price = '';
          }
          return next;
        })
      }
    />
  );
}

function DesignSectionReadOnly({ designSnapshot, selectedDesign }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const sampleNo = designSnapshot?.designSampleNo || selectedDesign?.sampleNo || '—';
  const concept = designSnapshot?.designConceptName || selectedDesign?.conceptName || '';
  const category = designSnapshot?.designCategoryName || selectedDesign?.categoryName || '';
  const sub = designSnapshot?.designSubCategoryName || selectedDesign?.subCategoryName || '';
  const note = String(selectedDesign?.note || '').trim();
  const image = designSnapshot?.designImage || selectedDesign?.designImage;
  const previewDesign = selectedDesign || {
    designImage: image,
    sampleNo: designSnapshot?.designSampleNo,
    conceptName: designSnapshot?.designConceptName,
    categoryName: designSnapshot?.designCategoryName,
    subCategoryName: designSnapshot?.designSubCategoryName,
    note: '',
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <header className={SECTION_CARD_HEADER}>
        <div className={SECTION_CARD_HEADER_MAIN}>
          <span className={SECTION_CARD_ICON}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </span>
          <div className={SECTION_CARD_TITLE_COL}>
            <h3 className={SECTION_CARD_TITLE}>Design</h3>
          </div>
        </div>
      </header>
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:gap-5 sm:p-5">
        <button
          type="button"
          disabled={!image}
          onClick={() => image && setPreviewOpen(true)}
          className="group relative h-40 w-full shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-left sm:w-40 disabled:cursor-default"
          title={image ? 'Preview design' : undefined}
          aria-label={image ? `Preview design ${sampleNo}` : undefined}
        >
          {image ? (
            <>
              <AuthenticatedImage src={image} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]" />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-900/0 transition-colors group-hover:bg-slate-900/25">
                <span className="rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-slate-800 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                  View
                </span>
              </span>
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">No image</div>
          )}
        </button>
        <dl className="grid flex-1 grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
          <DetailField label="Design no." value={sampleNo} />
          <DetailField label="Concept" value={concept || '—'} />
          <DetailField label="Category" value={category || '—'} />
          <DetailField label="Sub category" value={sub || '—'} />
          {selectedDesign?.sampleDesign === true ? (
            <DetailField label="Type" value="Sample" />
          ) : null}
          <DetailField
            label="Note"
            value={note || '—'}
            span={note ? 2 : undefined}
          />
        </dl>
      </div>
      <DesignImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        design={previewDesign}
      />
    </section>
  );
}

// ----------------------------------------------------------------------------
// DesignPicker — combobox-style selector: banner + search + dense list
// ----------------------------------------------------------------------------
function DesignPicker({ designs, selectedDesign, onPick, autoFocusSearch = true, onCreateDesign }) {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewDesign, setPreviewDesign] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(() => !selectedDesign);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // Open picker automatically if nothing selected; stay closed once user picks
  useEffect(() => {
    if (!selectedDesign) setPickerOpen(true);
  }, [selectedDesign]);

  // Autofocus search when the picker opens (skipped when parent disables it, e.g. new order)
  useEffect(() => {
    if (!pickerOpen || !autoFocusSearch) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [pickerOpen, autoFocusSearch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q
      ? designs.slice()
      : designs.filter(
          (d) =>
            String(d.sampleNo || '').toLowerCase().includes(q) ||
            String(d.conceptName || '').toLowerCase().includes(q) ||
            String(d.categoryName || '').toLowerCase().includes(q) ||
            String(d.subCategoryName || '').toLowerCase().includes(q)
        );
    return list.sort((a, b) => {
      const ta = new Date(a?.createdAt || 0).getTime();
      const tb = new Date(b?.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [designs, search]);

  const visible = useMemo(() => filtered.slice(0, MAX_PICKER_ROWS), [filtered]);
  const overflow = filtered.length - visible.length;

  // Reset / clamp the highlighted row whenever the result set shrinks
  useEffect(() => {
    setActiveIndex((prev) => {
      if (visible.length === 0) return 0;
      return Math.min(prev, visible.length - 1);
    });
  }, [visible.length]);

  // Keep the highlighted row scrolled into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-pick-index="${activeIndex}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const commitPick = useCallback(
    (design) => {
      if (!design) return;
      onPick(design);
      setPickerOpen(false);
      setSearch('');
    },
    [onPick]
  );

  const onSearchKeyDown = (e) => {
    if (visible.length === 0 && e.key !== 'Escape') return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitPick(visible[activeIndex]);
    } else if (e.key === 'Escape' && selectedDesign) {
      e.preventDefault();
      setPickerOpen(false);
    }
  };

  const hasSearch = Boolean(search.trim());
  const clearSearch = () => {
    setSearch('');
    searchRef.current?.focus();
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <header className={SECTION_CARD_HEADER}>
        <div className={SECTION_CARD_HEADER_MAIN}>
          <span className={SECTION_CARD_ICON}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </span>
          <div className={SECTION_CARD_TITLE_COL}>
            <h3 className={SECTION_CARD_TITLE}>
              Design <span className="text-rose-600">*</span>
              {selectedDesign ? ` · ${selectedDesign.sampleNo}` : ''}
            </h3>
          </div>
        </div>
      </header>

      <div className="space-y-3 p-4 sm:p-5">
        {/* Selected banner */}
        {selectedDesign && (
          <div className="flex items-center gap-3 rounded-xl border border-primary-200 bg-primary-50/40 p-3 sm:p-4">
            <button
              type="button"
              disabled={!selectedDesign.designImage}
              onClick={() => selectedDesign.designImage && setPreviewDesign(selectedDesign)}
              className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-primary-100 bg-white text-left sm:h-24 sm:w-24 disabled:cursor-default"
              title={selectedDesign.designImage ? 'Preview design' : undefined}
              aria-label={
                selectedDesign.designImage
                  ? `Preview design ${selectedDesign.sampleNo || ''}`
                  : undefined
              }
            >
              {selectedDesign.designImage ? (
                <>
                  <AuthenticatedImage
                    src={selectedDesign.designImage}
                    alt=""
                    className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
                  />
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-900/0 transition-colors group-hover:bg-slate-900/30">
                    <span className="rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-semibold text-slate-800 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                      View
                    </span>
                  </span>
                </>
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">—</div>
              )}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-base font-semibold text-slate-900">
                  {selectedDesign.sampleNo}
                </span>
                {selectedDesign.sampleDesign === true ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800 ring-1 ring-amber-200">
                    Sample
                  </span>
                ) : null}
                {selectedDesign.conceptName ? (
                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-primary-700 ring-1 ring-primary-200">
                    {selectedDesign.conceptName}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 truncate text-sm text-slate-600">
                {[selectedDesign.categoryName, selectedDesign.subCategoryName]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </p>
              {String(selectedDesign.note || '').trim() ? (
                <p className="mt-1 line-clamp-2 text-sm text-slate-700" title={String(selectedDesign.note).trim()}>
                  <span className="font-semibold text-slate-500">Note:</span>{' '}
                  {String(selectedDesign.note).trim()}
                </p>
              ) : null}
              <p className="mt-0.5 text-xs text-slate-500">
                {Array.isArray(selectedDesign.feeders) ? selectedDesign.feeders.length : 0} feeder
                {Array.isArray(selectedDesign.feeders) && selectedDesign.feeders.length === 1 ? '' : 's'}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 rounded-lg"
              onClick={() => setPickerOpen((v) => !v)}
            >
              {pickerOpen ? 'Close' : 'Change'}
            </Button>
          </div>
        )}

        {/* Picker body */}
        {pickerOpen && (
          <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50/50 p-3 sm:p-4">
            {/* Search + counter */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
              <div className={`min-w-0 flex-1 ${FIELD_LABEL_STACK}`}>
                <label className={FIELD_LABEL} htmlFor="design-picker-search">
                  Find a design
                </label>
                <div className="relative isolate">
                  <svg
                    className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 110-16 8 8 0 010 16z" />
                  </svg>
                  <input
                    id="design-picker-search"
                    ref={searchRef}
                    name="design-picker-search"
                    type="text"
                    inputMode="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                    placeholder="Search design no., concept, or category"
                    title="Use arrow keys and Enter to select a design"
                    className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-10 text-base leading-normal text-slate-900 shadow-sm transition-all placeholder:text-slate-400 placeholder:opacity-100 hover:border-slate-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    role="combobox"
                    aria-expanded="true"
                    aria-controls="design-picker-list"
                    aria-activedescendant={
                      visible[activeIndex] ? `design-option-${visible[activeIndex].id}` : undefined
                    }
                  />
                  {hasSearch ? (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      aria-label="Clear search"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="shrink-0 flex flex-col items-stretch gap-2 sm:pb-2.5 sm:items-end">
                <div className="text-xs font-medium text-slate-500">
                  {filtered.length === designs.length
                    ? `${designs.length} design${designs.length === 1 ? '' : 's'}`
                    : `${filtered.length} of ${designs.length}`}
                </div>
                {onCreateDesign ? (
                  <Button type="button" variant="outline" size="sm" onClick={onCreateDesign}>
                    + Create design
                  </Button>
                ) : null}
              </div>
            </div>

            {/* Dense list */}
            <div
              id="design-picker-list"
              ref={listRef}
              role="listbox"
              aria-label="Designs"
              className="max-h-[min(52vh,520px)] overflow-y-auto rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 scroll-smooth"
            >
              {visible.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-slate-500">
                  {designs.length === 0 ? (
                    <div className="flex flex-col items-center gap-3">
                      <p>No designs yet.</p>
                      {onCreateDesign ? (
                        <Button type="button" size="sm" onClick={onCreateDesign}>
                          Create new design
                        </Button>
                      ) : (
                        <p>Create one in the Designs section first.</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p>No designs match your search.</p>
                      {hasSearch && (
                        <button
                          type="button"
                          onClick={clearSearch}
                          className="text-sm font-medium text-primary-600 hover:text-primary-700"
                        >
                          Clear search
                        </button>
                      )}
                    </>
                  )}
                </div>
              ) : (
                visible.map((d, i) => {
                  const isSelected = selectedDesign && String(selectedDesign.id) === String(d.id);
                  const isActive = i === activeIndex;
                  return (
                    <button
                      key={d.id}
                      id={`design-option-${d.id}`}
                      data-pick-index={i}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => commitPick(d)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={[
                        'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors',
                        isActive ? 'bg-primary-50/80' : 'bg-white',
                        isSelected ? 'ring-1 ring-inset ring-primary-300' : '',
                      ].join(' ')}
                    >
                      <div
                        role="presentation"
                        onClick={(e) => {
                          if (!d.designImage) return;
                          e.stopPropagation();
                          setPreviewDesign(d);
                        }}
                        onKeyDown={(e) => {
                          if (!d.designImage) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            setPreviewDesign(d);
                          }
                        }}
                        className={[
                          'relative h-11 w-11 shrink-0 self-center overflow-hidden rounded-md border border-slate-200 bg-slate-100 sm:h-12 sm:w-12',
                          d.designImage ? 'cursor-zoom-in ring-offset-1 hover:ring-2 hover:ring-primary-300' : '',
                        ].join(' ')}
                        title={d.designImage ? 'Preview design' : undefined}
                      >
                        {d.designImage ? (
                          <AuthenticatedImage
                            src={d.designImage}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                            thumbnail
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">—</div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 self-center">
                        <div className="truncate text-sm font-semibold text-slate-900 sm:text-base">
                          {d.sampleNo}
                          {d.sampleDesign === true ? (
                            <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                              Sample
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-0.5 truncate text-xs leading-snug text-slate-600">
                          {[d.conceptName, d.categoryName, d.subCategoryName].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <div className="hidden shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 sm:inline-block">
                        {Array.isArray(d.feeders) ? d.feeders.length : 0}F
                      </div>
                      {isSelected ? (
                        <svg className="h-4 w-4 shrink-0 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : null}
                    </button>
                  );
                })
              )}
              {overflow > 0 && (
                <div className="px-3 py-2 text-center text-[11px] font-medium text-slate-500">
                  +{overflow} more · refine your search to see them
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <DesignImagePreviewModal
        open={Boolean(previewDesign)}
        onClose={() => setPreviewDesign(null)}
        design={previewDesign}
      />
    </section>
  );
}

function formatOrderPriceDisplay(v) {
  const n = Number.parseFloat(String(v ?? '').trim());
  if (!Number.isFinite(n)) return v == null || v === '' ? '—' : String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ============================================================================
// OrderInfoSection — order date, price, remark, dispatch docs
// ============================================================================
export function OrderInfoSection({ readOnly, form, setForm, showCommercial = true, priceOptional = false }) {
  if (readOnly) {
    return (
      <DetailSection
        title="Order info"
        icon={(
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
        columns={3}
      >
        <DetailField label="Order date" value={formatDate(form.orderDate)} />
        {showCommercial ? (
          <>
            <DetailField
              label="Price"
              value={
                form.price != null && String(form.price).trim() !== ''
                  ? formatOrderPriceDisplay(form.price)
                  : (priceOptional ? '—' : formatOrderPriceDisplay(form.price))
              }
            />
            {form.dispatchInvoiceImage ? (
              <DetailField
                label="Invoice image"
                value={(
                  <AuthenticatedFileViewLink fileRef={form.dispatchInvoiceImage} />
                )}
              />
            ) : null}
            {form.dispatchLrImage ? (
              <DetailField
                label="LR image"
                value={(
                  <AuthenticatedFileViewLink fileRef={form.dispatchLrImage} />
                )}
              />
            ) : null}
          </>
        ) : null}
        <DetailField label="Remark" value={form.remark?.trim() ? form.remark : '—'} span="full" />
      </DetailSection>
    );
  }
  return (
    <DetailSection
      title="Order info"
      icon={(
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )}
      columns={3}
      bodyClassName={FIELD_INPUT_WRAP}
    >
      <Input
        label="Order date"
        type="date"
        value={form.orderDate}
        onChange={(e) => setForm((w) => ({ ...w, orderDate: e.target.value }))}
      />
      {showCommercial ? (
        <Input
          label={priceOptional ? 'Price (optional)' : 'Price *'}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={form.price}
          onChange={(e) => setForm((w) => ({ ...w, price: e.target.value }))}
          placeholder={priceOptional ? 'Optional for Sample orders' : undefined}
        />
      ) : null}
      <div className="sm:col-span-2 lg:col-span-2">
        <Input
          label="Remark"
          value={form.remark}
          onChange={(e) => setForm((w) => ({ ...w, remark: e.target.value }))}
          placeholder="Notes for production or billing (optional)"
        />
      </div>
    </DetailSection>
  );
}

// ============================================================================
// StageVendorSection — outsourced stage vendor pickers (or summary)
// Only shown when order is at one of those stages.
// ============================================================================
export function StageVendorSection({ readOnly, form, setForm, vendorParties, workflowStatus }) {
  const showButta = WORKFLOW_SHOWS_BUTTA_CUTTING.has(workflowStatus);
  const showMill = WORKFLOW_SHOWS_MILL_FINISH.has(workflowStatus);
  const showBorder = WORKFLOW_SHOWS_BORDER_CUTTING.has(workflowStatus);
  const showHand = WORKFLOW_SHOWS_HANDWORK.has(workflowStatus);
  if (!showButta && !showMill && !showBorder && !showHand) return null;

  const vendorName = (id, list) => {
    const s = String(id || '').trim();
    if (!s) return '—';
    const row = (list || []).find((p) => String(p.id) === s);
    return row?.name || `#${s}`;
  };

  if (readOnly) {
    return (
      <DetailSection
        title="Stage vendors"
        icon={(
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h2l.4 2M7 13h10l4-8H5.4M7 13l-.4 2H19M7 13l-2.5 5M17 21a2 2 0 100-4 2 2 0 000 4zm-8 0a2 2 0 100-4 2 2 0 000 4z" />
          </svg>
        )}
        columns={2}
      >
        {showButta && (
          <>
            <DetailField label="Butta cutting party" value={vendorName(form.stageContext?.buttaCuttingPartyId, vendorParties.buttaCutting)} />
            <DetailField label="B. Pcs" value={form.stageContext?.buttaCuttingBpcs || '—'} mono />
          </>
        )}
        {showMill && (
          <>
            <DetailField label="Mill finish party" value={vendorName(form.stageContext?.millFinishPartyId, vendorParties.millFinish)} />
            <DetailField label="M. Pcs" value={form.stageContext?.millFinishBpcs || '—'} mono />
          </>
        )}
        {showBorder && (
          <>
            <DetailField label="Border cutting party" value={vendorName(form.stageContext?.borderCuttingPartyId, vendorParties.borderCutting)} />
            <DetailField label="BC. Pcs" value={form.stageContext?.borderCuttingBpcs || '—'} mono />
          </>
        )}
        {showHand && (
          <>
            <DetailField label="Handwork party" value={vendorName(form.stageContext?.handworkPartyId, vendorParties.handwork)} />
            <DetailField label="H. Pcs" value={form.stageContext?.handworkBpcs || '—'} mono />
          </>
        )}
      </DetailSection>
    );
  }

  const setCtx = (patch) =>
    setForm((w) => ({
      ...w,
      stageContext: { ...(w.stageContext || STAGE_CONTEXT_EMPTY), ...patch },
    }));
  const ctx = form.stageContext || STAGE_CONTEXT_EMPTY;

  return (
    <DetailSection
      title="Stage vendors"
      icon={(
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h2l.4 2M7 13h10l4-8H5.4M7 13l-.4 2H19M7 13l-2.5 5M17 21a2 2 0 100-4 2 2 0 000 4zm-8 0a2 2 0 100-4 2 2 0 000 4z" />
        </svg>
      )}
      columns={2}
      bodyClassName={FIELD_INPUT_WRAP}
    >
      {showButta && (
        <>
          <div>
            <label className={FIELD_LABEL}>Butta cutting party</label>
            <SearchableSelect
              value={ctx.buttaCuttingPartyId}
              placeholder="Type to find party…"
              emptyMessage="No party starts with that text"
              options={(vendorParties.buttaCutting || []).map((p) => ({
                value: String(p.id),
                label: p.name || '',
              }))}
              onChange={(id) => setCtx({ buttaCuttingPartyId: id })}
            />
          </div>
          <Input
            label="B. Pcs"
            type="number"
            min={0}
            value={ctx.buttaCuttingBpcs}
            onChange={(e) => setCtx({ buttaCuttingBpcs: e.target.value })}
          />
        </>
      )}
      {showMill && (
        <>
          <div>
            <label className={FIELD_LABEL}>Mill finish party</label>
            <SearchableSelect
              value={ctx.millFinishPartyId}
              placeholder="Type to find party…"
              emptyMessage="No party starts with that text"
              options={(vendorParties.millFinish || []).map((p) => ({
                value: String(p.id),
                label: p.name || '',
              }))}
              onChange={(id) => setCtx({ millFinishPartyId: id })}
            />
          </div>
          <Input
            label="M. Pcs"
            type="number"
            min={0}
            value={ctx.millFinishBpcs}
            onChange={(e) => setCtx({ millFinishBpcs: e.target.value })}
          />
        </>
      )}
      {showBorder && (
        <>
          <div>
            <label className={FIELD_LABEL}>Border cutting party</label>
            <SearchableSelect
              value={ctx.borderCuttingPartyId}
              placeholder="Type to find party…"
              emptyMessage="No party starts with that text"
              options={(vendorParties.borderCutting || []).map((p) => ({
                value: String(p.id),
                label: p.name || '',
              }))}
              onChange={(id) => setCtx({ borderCuttingPartyId: id })}
            />
          </div>
          <Input
            label="BC. Pcs"
            type="number"
            min={0}
            value={ctx.borderCuttingBpcs}
            onChange={(e) => setCtx({ borderCuttingBpcs: e.target.value })}
          />
        </>
      )}
      {showHand && (
        <>
          <div>
            <label className={FIELD_LABEL}>Handwork party</label>
            <SearchableSelect
              value={ctx.handworkPartyId}
              placeholder="Type to find party…"
              emptyMessage="No party starts with that text"
              options={(vendorParties.handwork || []).map((p) => ({
                value: String(p.id),
                label: p.name || '',
              }))}
              onChange={(id) => setCtx({ handworkPartyId: id })}
            />
          </div>
          <Input
            label="H. Pcs"
            type="number"
            min={0}
            value={ctx.handworkBpcs}
            onChange={(e) => setCtx({ handworkBpcs: e.target.value })}
          />
        </>
      )}
    </DetailSection>
  );
}

function BeamPauseIcon() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
    </svg>
  );
}

/** Full-width status strip + stacked actions — same footprint for every state (operator / tablet). */
const BEAM_STATUS_STRIP = {
  slate: 'border-slate-200 bg-slate-100 text-slate-900',
  teal: 'border-teal-200 bg-teal-50 text-teal-950',
  amber: 'border-amber-200 bg-amber-50 text-amber-950',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-950',
};

function BeamPlanRowStatus({ runtimeBeam, canActivate, onBeamAction }) {
  if (!runtimeBeam) {
    return <span className="text-[10px] text-slate-400">—</span>;
  }
  const meta = beamStatusMeta(runtimeBeam.status);
  const isTerminal = isBeamTerminalStatus(runtimeBeam.status);
  const isRunning = runtimeBeam.status === 'RUNNING';
  const isPending = runtimeBeam.status === 'PENDING';
  const isOnHold = runtimeBeam.status === 'ON_HOLD';
  const stripTone = BEAM_STATUS_STRIP[meta.tone] || BEAM_STATUS_STRIP.slate;

  const showActions = Boolean(canActivate && !isTerminal && onBeamAction);

  return (
    <div className="mx-auto flex w-full max-w-full flex-col gap-1.5">
      <div
        className={[
          'rounded-lg border px-2 py-2 text-center text-[11px] font-bold leading-tight shadow-sm sm:text-xs',
          stripTone,
        ].join(' ')}
      >
        <span className="inline-flex items-center justify-center gap-2">
          <span className={`h-2 w-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
          {meta.label}
        </span>
      </div>

      {showActions ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50/90 p-1 shadow-inner">
          {isPending ? (
            <Button block size="sm" variant="primary" className="min-h-[2.25rem] text-[12px] font-bold" onClick={() => onBeamAction('START', runtimeBeam)}>
              Start
            </Button>
          ) : null}
          {isRunning ? (
            <div className="grid grid-cols-[1fr_2.75rem] gap-1">
              <Button block size="sm" variant="success" className="min-h-[2.25rem] text-[12px] font-bold" onClick={() => onBeamAction('COMPLETE', runtimeBeam)}>
                Complete
              </Button>
              <button
                type="button"
                className="flex min-h-[2.25rem] items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                title="Pause"
                aria-label="Pause this line"
                onClick={() => onBeamAction('HOLD', runtimeBeam)}
              >
                <BeamPauseIcon />
              </button>
            </div>
          ) : null}
          {isOnHold ? (
            <div className="grid grid-cols-2 gap-1">
              <Button block size="sm" variant="primary" className="min-h-[2.25rem] text-[11px] font-bold sm:text-[12px]" onClick={() => onBeamAction('RESUME', runtimeBeam)}>
                Resume
              </Button>
              <Button block size="sm" variant="success" className="min-h-[2.25rem] text-[11px] font-bold sm:text-[12px]" onClick={() => onBeamAction('COMPLETE', runtimeBeam)}>
                Complete
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// BeamPlanSection — the big beam + feeder grid, editable or read-only
// ============================================================================
export function BeamPlanSection({
  readOnly,
  form,
  setForm,
  designs,
  colorOptions,
  openPickerAnchor,
  setOpenPickerAnchor,
  /** Server `order.beams` — when set in read-only mode, adds a Status column with actions. */
  runtimeBeams,
  onBeamAction,
  /** Machine + PCS stay correctable until production is complete; everything else is locked. */
  canEditBeamDetails = false,
  onSaveBeamDetails,
  beamDetailsSaving = false,
  machineOptions = [],
  workflowStatus,
  orderOnHold,
  /** When set, shows per-beam PDF checkboxes and a download action in the section header. */
  pdfExport,
  /** When set in edit mode, shows “Clone from order” for beam/feeder colours. */
  cloneFromOrder,
  /** When set ({ count, onClick, busy }), shows “Complete all running” in the section header. */
  completeAllRunning,
}) {
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [beamDetailsEditing, setBeamDetailsEditing] = useState(false);
  const [beamDetailsDraft, setBeamDetailsDraft] = useState({});
  const [beamDetailsError, setBeamDetailsError] = useState('');
  const selectedDesign = useMemo(
    () => designs.find((d) => String(d.id) === String(form.designId)),
    [designs, form.designId]
  );
  const isSingleBeamDesign = selectedDesign?.singleBeam === true;
  const singleBeamDisplayName = String(selectedDesign?.beamName || '').trim();
  const feedersList = useMemo(() => sortDesignFeeders(selectedDesign?.feeders), [selectedDesign]);
  const beams = form.beamPlan?.beams || [];
  const n = feedersList.length || (beams[0]?.feeders?.length ?? 0);

  /**
   * Plan beam_no → all ledger rows (machine lines), sorted by machineSlot.
   * Single-beam designs: all ledger rows are shown on the first plan row even if API ever used wrong beam_no.
   */
  const beamRowsByPlanNo = useMemo(() => {
    const m = new Map();
    const forceSingleBeamBucket = beams.length === 1 && isSingleBeamDesign;
    (runtimeBeams || []).forEach((raw) => {
      if (!raw || typeof raw !== 'object') return;
      const beamNoRaw = raw.beamNo ?? raw.beam_no;
      if (!forceSingleBeamBucket && (beamNoRaw == null || beamNoRaw === '')) return;
      const key = forceSingleBeamBucket ? 1 : Number(beamNoRaw);
      if (!forceSingleBeamBucket && !Number.isFinite(key)) return;
      const normalized = {
        ...raw,
        beamNo: key,
        machineSlot: Number(raw.machineSlot ?? raw.machine_slot) || 1,
      };
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(normalized);
    });
    m.forEach((list) => {
      list.sort((a, z) => (Number(a.machineSlot) || 1) - (Number(z.machineSlot) || 1));
    });
    return m;
  }, [runtimeBeams, beams.length, isSingleBeamDesign]);

  const showStatusColumn =
    readOnly && Array.isArray(runtimeBeams) && runtimeBeams.length > 0;

  const hasEditableBeamDetailsRows = useMemo(
    () => (runtimeBeams || []).some((row) => row?.id != null && !isBeamTerminalStatus(row.status)),
    [runtimeBeams]
  );
  const canToggleBeamDetails = Boolean(
    canEditBeamDetails
    && showStatusColumn
    && hasEditableBeamDetailsRows
    && typeof onSaveBeamDetails === 'function'
  );

  useEffect(() => {
    if (!canToggleBeamDetails && beamDetailsEditing) {
      setBeamDetailsEditing(false);
      setBeamDetailsDraft({});
      setBeamDetailsError('');
    }
  }, [canToggleBeamDetails, beamDetailsEditing]);

  /**
   * Keep the in-progress edit draft aligned with live ledger rows.
   * Needed when Start (or split start) runs while Machine/PCS edit is open —
   * new machine numbers / new ledger ids must appear without Cancel → re-open.
   */
  useEffect(() => {
    if (!beamDetailsEditing) return;
    setBeamDetailsDraft((prev) => {
      const next = {};
      let changed = false;
      for (const row of runtimeBeams || []) {
        if (row?.id == null || isBeamTerminalStatus(row.status)) continue;
        const id = String(row.id);
        const serverMachine = String(row.machineNo || '');
        const serverPcs = String(row.plannedPieces ?? '');
        const existing = prev[id];
        if (!existing) {
          next[id] = { machineNo: serverMachine, plannedPieces: serverPcs };
          changed = true;
          continue;
        }
        let machineNo = existing.machineNo;
        // After Start, draft still has blank machine while the server now has one.
        if (!String(machineNo || '').trim() && serverMachine) {
          machineNo = serverMachine;
          changed = true;
        }
        next[id] = {
          machineNo,
          plannedPieces: existing.plannedPieces,
        };
        if (
          machineNo !== existing.machineNo
          || existing.plannedPieces !== next[id].plannedPieces
        ) {
          changed = true;
        }
      }
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length !== nextKeys.length || prevKeys.some((k) => !(k in next))) {
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [runtimeBeams, beamDetailsEditing]);

  const machineSelectOptions = useMemo(
    () =>
      (machineOptions || [])
        .map((m) => String(m?.machineNo || '').trim())
        .filter(Boolean),
    [machineOptions]
  );

  const openBeamDetailsEdit = () => {
    const draft = {};
    (runtimeBeams || []).forEach((row) => {
      // Completed lines are frozen — only open / paused / not-started rows are editable.
      if (row?.id == null || isBeamTerminalStatus(row.status)) return;
      draft[String(row.id)] = {
        machineNo: String(row.machineNo || ''),
        plannedPieces: String(row.plannedPieces ?? ''),
      };
    });
    setBeamDetailsDraft(draft);
    setBeamDetailsError('');
    setBeamDetailsEditing(true);
  };

  const closeBeamDetailsEdit = () => {
    setBeamDetailsEditing(false);
    setBeamDetailsDraft({});
    setBeamDetailsError('');
  };

  const setBeamDetailField = (ledgerId, patch) =>
    setBeamDetailsDraft((prev) => ({
      ...prev,
      [String(ledgerId)]: { ...prev[String(ledgerId)], ...patch },
    }));

  /** Validates every edited row, then sends only the rows that actually changed. */
  const submitBeamDetails = async () => {
    const changes = [];
    for (const row of runtimeBeams || []) {
      if (row?.id == null || isBeamTerminalStatus(row.status)) continue;
      const draft = beamDetailsDraft[String(row.id)];
      if (!draft) continue;
      const label = `Beam #${row.beamNo}${Number(row.machineSlot) > 1 ? ` line ${row.machineSlot}` : ''}`;
      const pieces = Number.parseInt(String(draft.plannedPieces ?? '').trim(), 10);
      const machineStarted = row.status === 'RUNNING' || row.status === 'ON_HOLD';
      // Machine is chosen at Start for PENDING lines — never overwrite it here.
      const machineNo = machineStarted
        ? String(draft.machineNo || '').trim()
        : String(row.machineNo || '').trim();
      const accounted = (Number(row.producedPieces) || 0) + (Number(row.scrappedPieces) || 0);
      if (!Number.isFinite(pieces) || pieces < 1) {
        setBeamDetailsError(`${label}: PCS must be at least 1.`);
        return;
      }
      if (pieces < accounted) {
        setBeamDetailsError(`${label}: PCS cannot be below produced + scrapped (${accounted}).`);
        return;
      }
      if (machineStarted && !machineNo) {
        setBeamDetailsError(`${label}: machine is required once the beam has started.`);
        return;
      }
      const changed =
        (machineStarted && machineNo !== String(row.machineNo || '').trim())
        || pieces !== (Number(row.plannedPieces) || 0);
      if (changed) {
        changes.push({
          id: row.id,
          machineNo: machineStarted ? machineNo : (String(row.machineNo || '').trim() || null),
          plannedPieces: pieces,
        });
      }
    }
    if (changes.length === 0) {
      closeBeamDetailsEdit();
      return;
    }
    setBeamDetailsError('');
    const ok = await onSaveBeamDetails(changes);
    if (ok) closeBeamDetailsEdit();
  };

  const displayedPiecesTotal = showStatusColumn
    ? (runtimeBeams || []).reduce(
        (sum, row) => sum + (Number(row?.plannedPieces) || 0),
        0
      )
    : beamPiecesTotal(beams);
  const productionWindow =
    workflowStatus === 'PLACE_ORDER' || workflowStatus === 'IN_PRODUCTION';
  const canActivateBeams = Boolean(
    productionWindow && !orderOnHold && typeof onBeamAction === 'function'
  );

  const showPdfColumn = Boolean(pdfExport?.enabled && beams.length > 0);
  const pdfSelected = pdfExport?.selectedRows;
  const exportableRowIds = pdfExport?.exportableRowIds ?? [];
  const exportableCount = exportableRowIds.length;
  const allPdfSelected =
    showPdfColumn && exportableCount > 0 && pdfSelected && pdfSelected.size >= exportableCount;
  const somePdfSelected =
    showPdfColumn && pdfSelected && pdfSelected.size > 0 && !allPdfSelected;

  const renderPdfCheckboxCell = (ledgerRow) => {
    if (!showPdfColumn) return null;
    const exportable = ledgerRow && (pdfExport.isRowExportable?.(ledgerRow) ?? false);
    if (!exportable) {
      return (
        <td className={`${BT_TD} min-w-[2.25rem] px-0.5 text-center`}>
          <span className="text-[10px] text-slate-400">—</span>
        </td>
      );
    }
    const rowId = String(ledgerRow.id);
    return (
      <td className={`${BT_TD} min-w-[2.25rem] px-0.5 text-center`}>
        <input
          type="checkbox"
          className="h-3.5 w-3.5 rounded border-slate-400 text-primary-600 focus:ring-primary-500"
          checked={pdfSelected?.has(rowId) ?? false}
          onChange={() => pdfExport.onToggleRow(rowId)}
          aria-label={`Include machine ${ledgerRow.machineNo || ledgerRow.machine_no || ''} in PDF`}
        />
      </td>
    );
  };

  const beamDataRowCount = useMemo(() => {
    if (!beams.length) return 0;
    let count = 0;
    beams.forEach((_, bi) => {
      const ledgerRows = beamRowsByPlanNo.get(bi + 1) || [];
      const splitLedgerAcrossRows = showStatusColumn && ledgerRows.length > 1;
      count += splitLedgerAcrossRows ? ledgerRows.length : 1;
    });
    return count;
  }, [beams, beamRowsByPlanNo, showStatusColumn]);

  const beamTableScrolls = beamDataRowCount > BEAM_VISIBLE_ROW_CAP;
  const totalCellClass = beamTableScrolls ? BT_TD_TOTAL_STICKY : BT_TD_TOTAL;

  const minWidth = `max(100%, calc(${
    showPdfColumn ? '2.5rem + ' : ''
  }3rem + 10.5rem + (${n} * 7.25rem) + 4rem + 3.5rem + 4.5rem + 3.5rem${
    showStatusColumn ? ' + 11.5rem' : ''
  }))`;

  const swatch = (code, title) =>
    code ? (
      <span
        className="mx-auto h-7 w-10 shrink-0 rounded-md border border-slate-400 shadow-inner sm:h-8 sm:w-12"
        style={{ backgroundColor: code }}
        title={title || ''}
      />
    ) : (
      <span className="mx-auto flex h-7 w-10 shrink-0 items-center justify-center rounded-md border border-dashed border-slate-300 text-[9px] text-slate-400 sm:h-8 sm:w-12">
        —
      </span>
    );

  const addBeam = () =>
    setForm((w) => {
      const first = w.beamPlan?.beams?.[0];
      const template = (first?.feeders || []).map((f) => ({
        ...f,
        colorId: '',
        colorName: '',
        colorCode: '',
      }));
      return {
        ...w,
        beamPlan: {
          beams: [
            ...(w.beamPlan?.beams || []),
            {
              ...DEFAULT_BEAM_ROW_QTY,
              beamColorId: '',
              beamColorName: '',
              beamColorCode: '',
              feeders: template,
            },
          ],
        },
      };
    });

  const removeBeam = (bi) =>
    setForm((w) => {
      const list = [...(w.beamPlan?.beams || [])];
      if (list.length <= 1) return w;
      list.splice(bi, 1);
      return { ...w, beamPlan: { beams: list } };
    });

  const setBeam = (bi, patch) =>
    setForm((w) => {
      const list = [...(w.beamPlan?.beams || [])];
      list[bi] = { ...list[bi], ...patch };
      return { ...w, beamPlan: { beams: list } };
    });

  const setFeeder = (bi, fi, patch) =>
    setForm((w) => {
      const list = [...(w.beamPlan?.beams || [])];
      const beam = list[bi] || {};
      const merged = mergeBeamFeedersWithDesign(beam.feeders, feedersList);
      merged[fi] = { ...merged[fi], ...patch };
      list[bi] = { ...beam, feeders: merged };
      return { ...w, beamPlan: { beams: list } };
    });

  // Always prefer the design's feeder list so view mode matches edit mode after draft reload.
  const headerFeeders = feedersList.length > 0 ? feedersList : (beams[0]?.feeders || []);

  const feedersForBeam = useCallback(
    (beam) => mergeBeamFeedersWithDesign(beam?.feeders, feedersList),
    [feedersList]
  );

  /**
   * # through R.PCS cells. When `ledgerRow` is set (read-only, this plan row bound to a ledger line),
   * PCS uses `plannedPieces` and UNIT shows that line’s machine (shop floor); otherwise plan PCS / unit.
   */
  const renderPlanColumns = (beam, bi, ledgerRow = null) => {
    const pcsReadOnly =
      ledgerRow != null
        ? ledgerRow.plannedPieces != null
          ? ledgerRow.plannedPieces
          : '—'
        : beam.pieces ?? '—';
    const unitReadOnly =
      ledgerRow != null ? String(ledgerRow.machineNo || '').trim() || '—' : beam.unit || '—';
    const detailsDraft =
      beamDetailsEditing
      && ledgerRow?.id != null
      && !isBeamTerminalStatus(ledgerRow.status)
        ? beamDetailsDraft[String(ledgerRow.id)]
        : null;
    return (
      <>
        <td className={BT_TD_NUM}>
          <div className="flex flex-col items-center gap-1">
            <span>{bi + 1}</span>
            {!readOnly && beams.length > 1 ? (
              <button
                type="button"
                className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-50"
                onClick={() => removeBeam(bi)}
              >
                Remove
              </button>
            ) : null}
          </div>
        </td>
        <td className={BT_TD_BEAM}>
          {readOnly ? (
            isSingleBeamDesign ? (
              <div className="mx-auto flex max-w-[11rem] flex-col items-center justify-center px-1 py-0.5">
                <p className="line-clamp-3 text-center text-[11px] font-semibold leading-snug text-slate-800 sm:text-xs">
                  {singleBeamDisplayName || '—'}
                </p>
              </div>
            ) : (
              <div className="mx-auto flex max-w-[10rem] flex-col items-center gap-1">
                {swatch(beam.beamColorCode, beam.beamColorName)}
                {beam.beamColorName ? (
                  <p className="line-clamp-2 w-full text-[9px] font-semibold uppercase leading-tight text-slate-600 sm:text-[10px]">
                    {beam.beamColorName}
                  </p>
                ) : null}
              </div>
            )
          ) : isSingleBeamDesign ? (
            <div className="flex flex-col items-center justify-center px-2 py-1">
              <p
                className="line-clamp-3 text-center text-[11px] font-semibold leading-snug text-slate-800 sm:text-xs"
                title={singleBeamDisplayName || undefined}
              >
                {singleBeamDisplayName || '—'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-stretch gap-1">
              <BeamTableColorPicker
                anchorId={`beam-${bi}`}
                openAnchorId={openPickerAnchor}
                setOpenAnchorId={setOpenPickerAnchor}
                value={beam.beamColorId}
                colorName={beam.beamColorName}
                colorCode={beam.beamColorCode}
                options={colorOptions}
                disabled={!colorOptions.length}
                placeholder={colorOptions.length ? 'Beam color' : 'No colors yet'}
                aria-label={`Beam row ${bi + 1} color`}
                onSelect={(opt) =>
                  setBeam(bi, {
                    beamColorId: opt ? String(opt.id) : '',
                    beamColorName: opt?.name || '',
                    beamColorCode: opt?.colorCode || '',
                  })
                }
              />
              {beam.beamColorName ? (
                <p className="line-clamp-2 text-center text-[9px] font-semibold uppercase leading-tight text-slate-600 sm:text-[10px]">
                  {beam.beamColorName}
                </p>
              ) : null}
            </div>
          )}
        </td>
        {feedersForBeam(beam).map((row, fi) => (
          <td key={`${row.designFeederId || row.feederId || fi}-${fi}`} className={BT_TD_FEED}>
            {readOnly ? (
              <div className="mx-auto flex max-w-[10rem] flex-col items-center gap-1">
                {swatch(row.colorCode, row.colorName)}
                {row.colorName ? (
                  <p className="line-clamp-2 w-full break-words text-[9px] font-semibold leading-tight text-slate-600 sm:text-[10px]">
                    {row.colorName}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col items-stretch gap-1">
                <BeamTableColorPicker
                  anchorId={`feeder-${bi}-${fi}`}
                  openAnchorId={openPickerAnchor}
                  setOpenAnchorId={setOpenPickerAnchor}
                  value={row.colorId}
                  colorName={row.colorName}
                  colorCode={row.colorCode}
                  options={colorOptions}
                  disabled={!colorOptions.length}
                  placeholder={colorOptions.length ? 'Feeder color' : 'No colors yet'}
                  aria-label={`Beam ${bi + 1}, ${row.feederName || `feeder ${fi + 1}`} color`}
                  onSelect={(opt) =>
                    setFeeder(bi, fi, {
                      colorId: opt ? String(opt.id) : '',
                      colorName: opt?.name || '',
                      colorCode: opt?.colorCode || '',
                    })
                  }
                />
                {row.colorName ? (
                  <p className="line-clamp-2 break-words text-center text-[9px] font-semibold leading-tight text-slate-600 sm:text-[10px]">
                    {row.colorName}
                  </p>
                ) : null}
              </div>
            )}
          </td>
        ))}
        <td className={BT_TD_PCS}>
          {detailsDraft ? (
            <input
              type="number"
              min={1}
              className="w-full rounded-md border border-primary-300 bg-primary-50/40 px-1 py-1.5 text-center text-[11px] font-semibold tabular-nums shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 sm:text-xs"
              value={detailsDraft.plannedPieces}
              onChange={(e) => setBeamDetailField(ledgerRow.id, { plannedPieces: e.target.value })}
              aria-label={`Beam ${ledgerRow.beamNo} planned pieces`}
            />
          ) : readOnly ? (
            <span className="font-semibold tabular-nums">{pcsReadOnly}</span>
          ) : (
            <input
              type="number"
              min={0}
              className="w-full rounded-md border border-slate-200 bg-white px-1 py-1.5 text-center text-[11px] font-semibold tabular-nums shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 sm:text-xs"
              value={beam.pieces ?? ''}
              onChange={(e) => setBeam(bi, { pieces: e.target.value })}
            />
          )}
        </td>
        <td className={BT_TD_UNIT}>
          {detailsDraft
          && (ledgerRow.status === 'RUNNING' || ledgerRow.status === 'ON_HOLD') ? (
            machineSelectOptions.length > 0 ? (
              <SearchableSelect
                value={detailsDraft.machineNo}
                onChange={(id) => setBeamDetailField(ledgerRow.id, { machineNo: id })}
                placeholder="—"
                emptyMessage="No machine starts with that text"
                className="rounded-md border-primary-300 bg-primary-50/40 px-1 py-1.5 text-center text-[11px] font-semibold sm:text-xs"
                options={machineSelectOptions.map((no) => ({
                  value: String(no),
                  label: String(no),
                }))}
              />
            ) : (
              <input
                type="text"
                className="w-full rounded-md border border-primary-300 bg-primary-50/40 px-1 py-1.5 text-center text-[11px] font-semibold shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 sm:text-xs"
                value={detailsDraft.machineNo}
                onChange={(e) => setBeamDetailField(ledgerRow.id, { machineNo: e.target.value })}
                aria-label={`Beam ${ledgerRow.beamNo} machine`}
              />
            )
          ) : (
            <span
              className="font-semibold text-slate-500"
              title={
                ledgerRow != null
                  ? ledgerRow.status === 'PENDING'
                    ? 'Machine is selected when you start this beam'
                    : 'Machine on this line'
                  : readOnly
                    ? undefined
                    : 'Machine is assigned when the beam is started'
              }
            >
              {readOnly ? unitReadOnly : '—'}
            </span>
          )}
        </td>
        <td className={BT_TD_TAKA}>
          {readOnly ? (
            <span>{beam.takaNo?.trim() ? beam.takaNo : '—'}</span>
          ) : (
            <input
              type="text"
              className="w-full rounded-md border border-slate-200 bg-white px-1 py-1.5 text-center text-[10px] shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 sm:text-xs"
              value={beam.takaNo ?? ''}
              onChange={(e) => setBeam(bi, { takaNo: e.target.value })}
            />
          )}
        </td>
        <td className={BT_TD_RPCS}>
          {readOnly ? (
            <span className="tabular-nums">{beam.rPcs ?? '—'}</span>
          ) : (
            <input
              type="number"
              min={0}
              className="w-full rounded-md border border-slate-200 bg-white px-1 py-1.5 text-center text-[11px] tabular-nums shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 sm:text-xs"
              value={beam.rPcs ?? '0'}
              onChange={(e) => setBeam(bi, { rPcs: e.target.value })}
            />
          )}
        </td>
      </>
    );
  };

  if (beams.length === 0 && readOnly) return null;

  return (
    <section id="order-beam-plan" className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <header className={SECTION_CARD_HEADER}>
        <div className={SECTION_CARD_HEADER_MAIN}>
          <span className={SECTION_CARD_ICON}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343" />
            </svg>
          </span>
          <div className={SECTION_CARD_TITLE_COL}>
            <h3 className={SECTION_CARD_TITLE}>Beam &amp; feeder plan</h3>
          </div>
        </div>
        <div className={`${SECTION_CARD_ACTIONS} flex flex-wrap items-center gap-2`}>
          {completeAllRunning && !beamDetailsEditing ? (
            <Button
              type="button"
              variant="success"
              size="md"
              className="shrink-0 rounded-lg"
              disabled={completeAllRunning.busy}
              onClick={completeAllRunning.onClick}
            >
              Complete all running ({completeAllRunning.count})
            </Button>
          ) : null}
          {canToggleBeamDetails ? (
            beamDetailsEditing ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  className="shrink-0 rounded-lg"
                  disabled={beamDetailsSaving}
                  onClick={closeBeamDetailsEdit}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="shrink-0 rounded-lg"
                  loading={beamDetailsSaving}
                  disabled={beamDetailsSaving}
                  onClick={submitBeamDetails}
                >
                  Save machine / PCS
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="md"
                className="shrink-0 rounded-lg"
                onClick={openBeamDetailsEdit}
              >
                Edit machine / PCS
              </Button>
            )
          ) : null}
          {showPdfColumn ? (
            <Button
              type="button"
              variant="outline"
              size="md"
              className="shrink-0 rounded-lg"
              loading={pdfExport.downloading}
              disabled={pdfExport.downloading || !pdfSelected?.size}
              onClick={pdfExport.onDownload}
            >
              Download PDF
            </Button>
          ) : null}
          {!readOnly && cloneFromOrder?.enabled ? (
            <Button
              type="button"
              variant="outline"
              size="md"
              className="shrink-0 rounded-lg"
              disabled={!String(form.designId || '').trim() || feedersList.length === 0}
              onClick={() => setCloneModalOpen(true)}
            >
              Clone from order
            </Button>
          ) : null}
          {!readOnly && (
            <div className="relative group/add-beam">
              <Button
                type="button"
                variant="outline"
                size="md"
                className="shrink-0 rounded-lg"
                disabled={feedersList.length === 0 || isSingleBeamDesign}
                onClick={addBeam}
              >
                Add beam
              </Button>
              {isSingleBeamDesign && (
                <div className="pointer-events-none absolute right-0 top-full z-20 mt-1 hidden rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900 shadow-sm group-hover/add-beam:block whitespace-nowrap">
                  Only multi-beam designs allow multiple beams.
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <div className="p-2 sm:p-3">
        {beamDetailsEditing ? (
          <div className="mb-2 rounded-lg border border-primary-200 bg-primary-50/60 px-3 py-2 text-[11px] font-medium text-primary-900 sm:text-xs">
            PCS can be updated on open lines. Machine can be changed only after a beam is started.
            Completed lines stay locked.
          </div>
        ) : null}
        {beamDetailsError ? (
          <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-800 sm:text-xs">
            {beamDetailsError}
          </div>
        ) : null}
        {!readOnly && !String(form.designId || '').trim() ? (
          <p className="px-2 py-6 text-sm text-slate-600">
            Select a design to plan beams.
          </p>
        ) : !readOnly && feedersList.length === 0 ? (
          <p className="px-2 py-6 text-sm text-amber-800">
            This design has no feeders. Add feeders in Designs first.
          </p>
        ) : (
          <div className="relative rounded-2xl border border-slate-200 bg-white p-1 shadow-sm ring-1 ring-slate-900/[0.04] sm:p-1.5">
            <BeamTableScrollArea dataRowCount={beamDataRowCount}>
              <table
                className="w-max min-w-full border-collapse text-[11px] sm:text-xs"
                style={{ minWidth }}
              >
                <thead>
                  <tr>
                    {showPdfColumn ? (
                      <th className={`${BT_TH_STICKY} min-w-[2.25rem] px-0.5`} scope="col" title="Include in PDF">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-slate-400 text-primary-600 focus:ring-primary-500"
                          checked={allPdfSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = somePdfSelected;
                          }}
                          onChange={(e) => pdfExport.onToggleAll(e.target.checked)}
                          aria-label="Select all started machine lines for PDF"
                          disabled={exportableCount === 0}
                        />
                      </th>
                    ) : null}
                    <th className={BT_TH_STICKY} scope="col">#</th>
                    <th className={BT_TH_STICKY} scope="col">{isSingleBeamDesign ? 'Beam name' : 'BEAM'}</th>
                    {headerFeeders.map((f, hi) => (
                      <th
                        key={f.id ?? f.designFeederId ?? `fh-${hi}`}
                        className={`${BT_TH_STICKY} leading-tight`}
                        scope="col"
                      >
                        <div>F{hi + 1}</div>
                        <div className="mt-0.5 whitespace-normal break-words text-[9px] font-semibold uppercase tracking-wide text-slate-600 sm:text-[10px]">
                          ({feederHeaderShort(f.feederName)})
                        </div>
                      </th>
                    ))}
                    <th className={BT_TH_STICKY} scope="col">PCS</th>
                    <th className={BT_TH_STICKY} scope="col">{showStatusColumn ? 'Machine' : 'UNIT'}</th>
                    <th className={BT_TH_STICKY} scope="col">TAKA NO.</th>
                    <th className={BT_TH_STICKY} scope="col">R. PCS</th>
                    {showStatusColumn ? (
                      <th className={BT_TH_STICKY} scope="col">Status</th>
                    ) : null}
                  </tr>
                </thead>
                {beams.map((beam, bi) => {
                  const ledgerRows = beamRowsByPlanNo.get(bi + 1) || [];
                  const splitLedgerAcrossRows = showStatusColumn && ledgerRows.length > 1;

                  const pdfCheckboxCell = renderPdfCheckboxCell(
                    ledgerRows.length > 0 ? ledgerRows[0] : null
                  );

                  if (!splitLedgerAcrossRows) {
                    return (
                      <tbody key={`beam-row-${bi}`}>
                        <tr className="bg-white" data-beam-row>
                          {pdfCheckboxCell}
                          {renderPlanColumns(beam, bi, ledgerRows.length > 0 ? ledgerRows[0] : null)}
                          {showStatusColumn ? (
                            <td className={BT_TD_STATUS}>
                              {ledgerRows.length === 0 ? (
                                <span className="text-[10px] text-slate-400">—</span>
                              ) : (
                                <BeamPlanRowStatus
                                  runtimeBeam={ledgerRows[0]}
                                  canActivate={canActivateBeams}
                                  onBeamAction={onBeamAction}
                                />
                              )}
                            </td>
                          ) : null}
                        </tr>
                      </tbody>
                    );
                  }

                  return (
                    <tbody key={`beam-row-${bi}`}>
                      {ledgerRows.map((rb, ri) => (
                        <tr
                          key={rb.id != null ? `ledger-tr-${rb.id}` : `beam-${bi}-slot-${rb.machineSlot ?? ri}`}
                          className="bg-white"
                          data-beam-row
                        >
                          {renderPdfCheckboxCell(rb)}
                          {renderPlanColumns(beam, bi, rb)}
                          <td className={`${BT_TD_STATUS} align-middle`}>
                            <BeamPlanRowStatus
                              runtimeBeam={rb}
                              canActivate={canActivateBeams}
                              onBeamAction={onBeamAction}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  );
                })}
                <tbody key="beam-plan-totals">
                  <tr data-beam-totals>
                    {showPdfColumn ? <td className={totalCellClass} /> : null}
                    <td className={totalCellClass} colSpan={2 + headerFeeders.length}>TOTAL</td>
                    <td className={`${totalCellClass} tabular-nums`}>{displayedPiecesTotal}</td>
                    <td className={totalCellClass} />
                    <td className={totalCellClass} />
                    <td className={totalCellClass} />
                    {showStatusColumn ? <td className={totalCellClass} /> : null}
                  </tr>
                </tbody>
              </table>
            </BeamTableScrollArea>
          </div>
        )}
      </div>
      {cloneFromOrder?.enabled ? (
        <BeamPlanCloneModal
          open={cloneModalOpen}
          onClose={() => setCloneModalOpen(false)}
          authFetch={cloneFromOrder.authFetch}
          targetDesignId={form.designId}
          designs={designs}
          excludeOrderId={cloneFromOrder.excludeOrderId}
          toast={cloneFromOrder.toast}
          onApply={(beamPlan) => {
            cloneFromOrder.onApply?.(beamPlan);
            setCloneModalOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

// Small fragment helper since DetailSection sometimes takes `<></>` with fields
export function SectionChildren({ children }) {
  return <Fragment>{children}</Fragment>;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    return iso;
  }
}
